import {Button, Icon} from '@wandb/components';
import React, {useState} from 'react';

import type {NetworkAccessRequest} from '../client/WbAgentClient';
import {WBAgentNetworkDomainList} from './WBAgentNetworkDomainList';

export interface WBAgentNetworkAccessRequestProps {
  request: NetworkAccessRequest;
  /** Grant access to the selected domains. */
  onGrant: (domains: string[]) => void;
  /** Deny the request and continue without elevated access. */
  onDeny: () => void;
  /** Whether the controls are disabled (e.g. a run is in progress). */
  isDisabled?: boolean;
}

/**
 * WBAgentNetworkAccessRequest presents the elevated-network-access request an
 * agent attached to its last turn. The sandbox blocks most outbound domains by
 * default; the agent names the domains it needs and why. The user grants a
 * subset (default: all) or denies. A grant rides the next turn's permissions
 * patch, so the next turn's sandbox can reach the granted domains.
 */
export const WBAgentNetworkAccessRequest = ({
  request,
  onGrant,
  onDeny,
  isDisabled = false,
}: WBAgentNetworkAccessRequestProps) => {
  // Domains default to all-selected; the user can uncheck any before granting.
  const [selected, setSelected] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(request.domains.map(d => [d, true]))
  );

  const toggle = (domain: string) =>
    setSelected(prev => ({...prev, [domain]: !prev[domain]}));

  const grantedDomains = request.domains.filter(d => selected[d]);
  const canGrant = !isDisabled && grantedDomains.length > 0;

  return (
    <div className="mb-8 flex flex-col gap-12 rounded border border-wb-border bg-wb-surface p-12">
      <div className="flex items-center gap-8">
        <span className="inline-flex shrink-0">
          <Icon icon="lock-open" size="sm" color="sienna" />
        </span>
        <span className="text-sm font-semibold text-wb-text-primary">
          The agent is requesting network access
        </span>
      </div>

      <span className="text-sm text-wb-text-secondary">{request.reason}</span>

      <WBAgentNetworkDomainList
        domains={request.domains}
        selected={selected}
        onToggle={toggle}
      />

      <div className="flex justify-end gap-8">
        <Button variant="secondary" isDisabled={isDisabled} onPress={onDeny}>
          Deny
        </Button>
        <Button
          variant="primary"
          isDisabled={!canGrant}
          onPress={() => onGrant(grantedDomains)}>
          Grant access
        </Button>
      </div>
    </div>
  );
};
